import "../Home/Styles.css";
import LegalPageLinks from "../../components/LegalPageLinks";
import { CHURCH_INFO } from "../../utils/churchInfo";
import { usePageMeta } from "../../utils/usePageMeta";

const policySections = [
  {
    title: "1. Scope of This Policy",
    paragraphs: [
      "This Privacy Policy applies to Trinity Christian Church's website at www.trinity-cc.org, including the page located at /privacy, and to the Trinity Christian Church mobile application.",
      "It explains what information we collect, how we use it, when we share it, and the choices available to you when you use our digital services.",
    ],
  },
  {
    title: "2. Information We Collect",
    paragraphs: [
      "We may collect information you provide directly to us, information collected automatically when you use our services, and information received from service providers that help us operate the website and mobile app.",
    ],
    bullets: [
      "Contact information you choose to provide, such as your name, phone number, email address, prayer request details, or message contents.",
      "Donation-related information when you choose to give online, including transaction details and payment confirmation information supplied by our payment processor.",
      "Content and media you choose to upload or submit through website or mobile-app features.",
      "Device, browser, and app usage information such as IP address, browser type, operating system, device identifiers, referring pages, app interactions, and approximate diagnostic data.",
      "Locally stored data, including limited cached content and settings stored in browser local storage or on your device to improve performance and usability.",
    ],
  },
  {
    title: "3. How We Use Information",
    bullets: [
      "Provide, operate, and improve the website, mobile app, and church-related services.",
      "Display church content such as sermons, sermon notes, events, announcements, and ministry information.",
      "Process donations and maintain donation-related records.",
      "Respond to support requests, prayer requests, and ministry-related communications.",
      "Maintain security, prevent misuse, debug issues, and protect our services, users, and community.",
      "Comply with legal, tax, accounting, and recordkeeping obligations.",
    ],
  },
  {
    title: "4. Donations and Payment Processing",
    paragraphs: [
      "Online donations may be processed by third-party payment providers such as PayPal. Trinity Christian Church does not store full payment card numbers in this website codebase.",
      "When you make a donation, the payment provider may collect information necessary to process the transaction, issue a receipt, detect fraud, and comply with legal obligations. Your use of those payment services is also governed by the provider's own privacy policy and terms.",
    ],
  },
  {
    title: "5. Mobile App Data and Device Permissions",
    paragraphs: [
      "The Trinity Christian Church mobile app may collect the same categories of information described in this policy and may also process device-level information needed to provide app features.",
      "If a mobile feature requires access to device capabilities such as notifications, photos, camera, files, or storage, the app should request permission at the device level. You can manage those permissions through your device settings.",
    ],
  },
  {
    title: "6. Cookies, Local Storage, and Similar Technologies",
    paragraphs: [
      "We may use cookies, local storage, and similar technologies to keep the website and mobile app functioning, remember preferences, reduce repeated data requests, and improve performance.",
      "For example, the current website stores limited cached page content in local storage for a short period to improve load performance. You can clear browser storage through your browser or device settings, but some features may work less efficiently afterward.",
    ],
  },
  {
    title: "7. When We Share Information",
    paragraphs: [
      "We do not sell your personal information. We may share information only as reasonably necessary to operate our services and ministry activities.",
    ],
    bullets: [
      "With payment processors that handle donations and receipts.",
      "With hosting, storage, infrastructure, analytics, security, and technical service providers that help us run the website and mobile app.",
      "With social media, streaming, or linked third-party platforms when you choose to interact with those services.",
      "When required by law, legal process, or to protect rights, safety, property, users, or our organization.",
      "As part of an organizational transition, merger, or asset transfer, if legally permitted and operationally necessary.",
    ],
  },
  {
    title: "8. Data Retention and Security",
    paragraphs: [
      "We retain information for as long as reasonably necessary for the purposes described in this policy, including ministry operations, donations, tax reporting, legal compliance, dispute resolution, and security.",
      "We use administrative, technical, and organizational measures designed to protect information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.",
    ],
  },
  {
    title: "9. Your Choices and Rights",
    bullets: [
      "You may choose not to provide certain information, though some features may not function properly.",
      "You may manage browser cookies, local storage, and mobile-app permissions through your device or browser settings.",
      "If you would like to request access to, correction of, or deletion of information we hold about you, please contact Trinity Christian Church using the contact details listed below.",
      "If you interact with a third-party payment, app-store, video, or social platform, you may also need to contact that provider directly regarding information they control.",
    ],
  },
  {
    title: "10. Children's Privacy",
    paragraphs: [
      "Our digital services are intended for a general audience and ministry community. We do not knowingly collect personal information from children in violation of applicable law.",
      "If you believe a child has provided personal information through the website or mobile app without appropriate consent, please contact Trinity Christian Church so we can review and address the issue.",
    ],
  },
  {
    title: "11. Third-Party Links and Services",
    paragraphs: [
      "Our website and mobile app may contain links to third-party sites, services, videos, social-media pages, or app-store listings. We are not responsible for the privacy practices of those third parties, and we encourage you to review their policies before providing information.",
    ],
  },
  {
    title: "12. Changes to This Privacy Policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. When we do, we will post the revised version at www.trinity-cc.org/privacy and update the effective date shown below.",
    ],
  },
];

const sectionStyles = [
  {
    bar: "bg-tcc-blue",
    badge: "bg-tcc-blue/10 text-tcc-blue",
    title: "text-tcc-blue",
    glow: "shadow-tcc-blue/10",
  },
  {
    bar: "bg-tcc-green",
    badge: "bg-tcc-green/15 text-[#5f9024]",
    title: "text-[#5f9024]",
    glow: "shadow-tcc-green/10",
  },
  {
    bar: "bg-tcc-brown",
    badge: "bg-tcc-brown/10 text-tcc-brown",
    title: "text-tcc-brown",
    glow: "shadow-tcc-brown/10",
  },
];

const Privacy = () => {
  usePageMeta(
    "Trinity Christian Church Privacy Policy",
    "Read the Trinity Christian Church privacy policy for the website and mobile app, including sermons, giving, prayer requests, and app support."
  );

  return (
    <main className="min-h-screen bg-[#f4f1ec] text-slate-900">
      <section className="relative overflow-hidden bg-[#141414]">
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-3">
          <div className="bg-tcc-blue/35" />
          <div className="bg-tcc-green/25" />
          <div className="bg-tcc-brown/35" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.15),_transparent_38%)]" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-24">
          <a
            href="/"
            className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium text-white/85 backdrop-blur-sm transition hover:border-white/40 hover:bg-white/10 hover:text-white"
          >
            Back to Home
          </a>
          <div className="mt-10 max-w-4xl">
            <p className="text-sm uppercase tracking-[0.45em] text-white/70">
              Trinity Christian Church
            </p>
            <h1 className="mt-4 text-4xl text-white md:text-6xl">
              <span className="archivo-black-regular">Privacy</span>{" "}
              <i className="anton-regular-italic text-tcc-green">Policy</i>
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-white/85">
              This Privacy Policy applies to the Trinity Christian Church website
              and mobile app, including the public page at
              www.trinity-cc.org/privacy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm text-white/90">
              <span className="rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                Effective Date: March 28, 2026
              </span>
              <span className="rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm">
                Applies to web and mobile
              </span>
            </div>
            <LegalPageLinks current="privacy" />
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-8 max-w-6xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="rounded-[2rem] border border-black/5 bg-white p-6 shadow-[0_24px_80px_rgba(0,0,0,0.12)] md:p-10">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-3xl bg-[#f7f9fb] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                Privacy Email
              </p>
              <a
                href={`mailto:${CHURCH_INFO.email}`}
                className="mt-3 block text-lg font-semibold text-tcc-blue"
              >
                {CHURCH_INFO.email}
              </a>
            </div>
            <div className="rounded-3xl bg-[#f7f9fb] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                Phone
              </p>
              <a
                href={CHURCH_INFO.phoneHref}
                className="mt-3 block text-lg font-semibold text-tcc-blue"
              >
                {CHURCH_INFO.phoneDisplay}
              </a>
            </div>
            <div className="rounded-3xl bg-[#f7f9fb] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                Address
              </p>
              <p className="mt-3 text-lg font-semibold text-slate-800">
                {CHURCH_INFO.addressLine1}
              </p>
              <p className="text-sm text-slate-600">{CHURCH_INFO.addressLine2}</p>
            </div>
            <div className="rounded-3xl bg-[#f7f9fb] p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                Website
              </p>
              <a
                href={CHURCH_INFO.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 block text-lg font-semibold text-tcc-blue"
              >
                {CHURCH_INFO.websiteLabel}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6">
          {policySections.map((section, index) => {
            const style = sectionStyles[index % sectionStyles.length];

            return (
              <article
                key={section.title}
                className={`overflow-hidden rounded-[2rem] border border-black/5 bg-white shadow-[0_18px_55px_rgba(0,0,0,0.08)] ${style.glow}`}
              >
                <div className={`h-2 w-full ${style.bar}`} />
                <div className="p-6 md:p-8">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] ${style.badge}`}
                  >
                    Policy Section
                  </span>
                  <h2 className={`mt-4 text-2xl font-semibold ${style.title}`}>
                    {section.title}
                  </h2>

                  {section.paragraphs?.map((paragraph) => (
                    <p
                      key={paragraph}
                      className="mt-4 text-base leading-8 text-slate-700"
                    >
                      {paragraph}
                    </p>
                  ))}

                  {section.bullets?.length ? (
                    <ul className="mt-5 space-y-3 text-base leading-8 text-slate-700">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3">
                          <span
                            className={`mt-3 h-2.5 w-2.5 shrink-0 rounded-full ${style.bar}`}
                          />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default Privacy;
